import { useRef, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Download, FileUp, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, fileUrl } from "@/lib/api";
import { NO_MODEL } from "@/components/EquipmentLines";

const ErrorList = ({ errors }) => (
  <div className="max-h-40 space-y-1 overflow-y-auto rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-xs" data-testid="import-errors-list">
    {errors.map((e) => (
      <div key={e.row} data-testid={`import-error-row-${e.row}`}>
        <span className="font-mono text-amber-400">Fila {e.row}:</span> {e.errors.join(", ")}
        <span className="text-muted-foreground"> · {[e.values.tipo, e.values.marca, e.values.modelo, e.values.cantidad].filter(Boolean).join(" / ") || "fila vacía"}</span>
      </div>
    ))}
  </div>
);

const PreviewTable = ({ rows }) => (
  <div className="max-h-72 overflow-auto rounded-lg border border-border" data-testid="import-preview-table">
    <table className="w-full text-left text-xs">
      <thead className="sticky top-0 bg-card text-[11px] uppercase tracking-wider text-muted-foreground">
        <tr>{["Fila", "Tipo", "Marca", "Modelo", "Cantidad", "Estado"].map((h) => <th key={h} className="px-2 py-2">{h}</th>)}</tr>
      </thead>
      <tbody className="divide-y divide-border">
        {rows.map((r) => (
          <tr key={r.key} data-testid={`import-preview-row-${r.row}`}>
            <td className="px-2 py-1.5 font-mono text-muted-foreground">{r.row}</td>
            <td className="px-2 py-1.5">{r.type_name}</td>
            <td className="px-2 py-1.5">{r.brand || "—"}</td>
            <td className="px-2 py-1.5 font-mono">{r.model || <span className="italic text-muted-foreground">{NO_MODEL}</span>}</td>
            <td className="px-2 py-1.5 font-mono">{r.quantity}</td>
            <td className={`px-2 py-1.5 ${r.is_new ? "text-sky-400" : "text-emerald-400"}`}>{r.status}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const PlannedImport = ({ projectId, onConfirm }) => {
  const input = useRef(null);
  const [preview, setPreview] = useState(null);
  const [showErrors, setShowErrors] = useState(true);
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/projects/${projectId}/planned-equipment/import-preview`, fd);
      setPreview(data);
      setShowErrors(true);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); input.current.value = ""; }
  };

  const confirm = async () => {
    setBusy(true);
    let ok = false;
    try {
      const rows = preview.valid.map((r) => ({ type_name: r.type_name, brand: r.brand || "", model: r.model || "", description: r.description || "", quantity: r.quantity }));
      const { data } = await api.post(`/projects/${projectId}/planned-equipment/import-resolve`, { rows });
      if (data.created_types || data.created_models) toast.success(`Catálogo: ${data.created_models} modelos y ${data.created_types} tipos nuevos agregados automáticamente`);
      ok = await onConfirm(data.lines);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
    if (ok) setPreview(null);
  };

  return (
    <div className="flex flex-wrap gap-2" data-testid="planned-import-bar">
      <input ref={input} type="file" accept=".xlsx,.csv" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="planned-import-file-input" />
      <Button type="button" variant="outline" size="sm" onClick={() => input.current.click()} disabled={busy} data-testid="planned-import-button">
        {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileUp className="mr-2 h-4 w-4" />}Importar desde archivo
      </Button>
      <Button type="button" variant="ghost" size="sm" asChild data-testid="planned-template-xlsx"><a href={fileUrl("/projects/planned-equipment-template?fmt=xlsx")}><Download className="mr-1 h-4 w-4" />Plantilla XLSX</a></Button>
      <Button type="button" variant="ghost" size="sm" asChild data-testid="planned-template-csv"><a href={fileUrl("/projects/planned-equipment-template?fmt=csv")}><Download className="mr-1 h-4 w-4" />Plantilla CSV</a></Button>
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-3xl" data-testid="import-preview-dialog">
          <DialogHeader><DialogTitle className="font-display">Importar equipamiento</DialogTitle></DialogHeader>
          {preview && (
            <div className="space-y-3 text-sm">
              <p className="text-muted-foreground">Archivo: <span className="font-mono text-foreground" data-testid="import-filename">{preview.filename}</span></p>
              <p className="flex items-center gap-2 text-emerald-400" data-testid="import-valid-count"><CheckCircle2 className="h-4 w-4" />{preview.valid.length} equipos válidos</p>
              {preview.errors.length > 0 && (
                <div className="space-y-2">
                  <p className="flex items-center gap-2 text-amber-400" data-testid="import-error-count"><AlertTriangle className="h-4 w-4" />{preview.errors.length} filas con errores
                    <button type="button" className="text-xs underline" onClick={() => setShowErrors((v) => !v)} data-testid="import-toggle-errors">{showErrors ? "Ocultar errores" : "Ver errores"}</button></p>
                  {showErrors && <ErrorList errors={preview.errors} />}
                </div>
              )}
              <p className="text-xs text-muted-foreground">Las filas con errores no se importan. La importación agrega equipos al proyecto sin modificar los existentes. Los equipos nuevos se agregan automáticamente al catálogo al confirmar; los existentes se reutilizan sin cambios.</p>
              {preview.valid.length > 0 && <PreviewTable rows={preview.valid} />}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPreview(null)} data-testid="import-cancel-button">Cancelar</Button>
            <Button onClick={confirm} disabled={busy || !preview?.valid.length} data-testid="import-confirm-button">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Confirmar importación</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
